import React from 'react'
import { Grid, Typography, MenuItem, Theme } from '@material-ui/core'
import { useIntl } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import { Category } from '@src/types/graphql-types'
import LiquidSelect from '@src/components/LiquidSelect'
import {
  shouldRenderModificationCategory,
  shouldSkipEnabledModificationsCheck,
  useProductFromQueryParam,
} from '../../ProductsConfiguration'
interface ModificationDropDownBaseProps {
  disabled: boolean
  title: React.ReactNode
  description: React.ReactNode
  categoryModifications: Category[]
  onChange?: (e: string) => void
  value?: string
  onCategoryChange?: (e: string) => void
  categoryValue?: string
  enabledModifications?: string[]
}

const useStyle = makeStyles((theme: Theme) => {
  return {
    infoText: {
      fontSize: theme.typography.pxToRem(14),
    },
  }
})

const ModificationDropDownBase: React.FC<ModificationDropDownBaseProps> = ({
  disabled,
  title,
  description,
  categoryModifications,
  onChange,
  value,
  onCategoryChange: onCategoryChangeFunction,
  categoryValue,
  enabledModifications = [],
}) => {
  const classes = useStyle()
  const intl = useIntl()
  const product = useProductFromQueryParam()

  const allModifications = categoryModifications
    .filter((category) =>
      categoryValue && categoryValue !== '' && categoryValue !== 'all'
        ? category.categoryName === categoryValue
        : true
    )
    .flatMap((single) => single.modifications)

  const categoryList = [
    {
      categoryDisplayName: intl.formatMessage({
        id: 'MODIFICATIONS_ALL_CATEGORIES',
        defaultMessage: 'All',
      }),
      order: 0,
      value: 'all',
    },
  ].concat(
    categoryModifications
      .map((category) => ({
        categoryDisplayName: category.categoryDisplayName
          ? intl.formatMessage({
              id: category.categoryDisplayName,
              defaultMessage: category.categoryName,
            })
          : category.categoryName,
        order: category.order ? parseInt(category.order) : 0,
        value: category.categoryName,
      }))
      .sort((a, b) => a.order - b.order)
  )
  const skipEnabledModificationsCheck =
    shouldSkipEnabledModificationsCheck(product)
  const modificationList = [
    {
      displayName: intl.formatMessage({
        id: 'PC_SPEC_SELECT_antisera',
        defaultMessage: 'Select',
      }),
      order: 0,
      value: '',
    },
  ].concat(
    allModifications
      .filter((mod) =>
        skipEnabledModificationsCheck
          ? true
          : enabledModifications.includes(mod?.value)
      )
      .map((modification) => ({
        displayName: intl.formatMessage({
          id: modification.displayName,
          defaultMessage: modification.name,
        }),
        order: modification.order ? parseInt(modification.order) : 0,
        value: modification.value,
      }))
      .sort((a, b) => a.order - b.order)
  )

  const onCategoryChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    onCategoryChangeFunction &&
      onCategoryChangeFunction(e.target.value as string)
    if (e.target.value !== '' && e.target.value !== 'all') {
      //check modification comes under selected category
      const checkForMod = categoryModifications
        .find((category) => category.categoryName === e.target.value)
        ?.modifications.find((mod) => mod?.value === value)
      //if not reset to empty
      if (!checkForMod) {
        onChange && onChange('')
      }
    }
  }
  const onModificationChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    onChange && onChange(e.target.value as string)
  }
  const renderModValue = (v: any) => {
    const selectedMod = allModifications.find((mod) => mod?.value === v)
    if (selectedMod?.displayName && selectedMod?.name) {
      return intl.formatMessage({
        id: selectedMod.displayName,
        defaultMessage: selectedMod.name,
      })
    } else {
      return intl.formatMessage({
        id: 'MODIFICATIONS_SELECT',
        defaultMessage: 'Select',
      })
    }
  }

  const renderCategoryValue = (catValue: string | undefined) => {
    if (!catValue || catValue.trim() === '') {
      return intl.formatMessage({
        id: 'MODIFICATIONS_SELECT',
        defaultMessage: 'Select',
      })
    }
    const selectedCategory = categoryList.find((cat) => cat.value === catValue)
    if (selectedCategory) {
      return selectedCategory.categoryDisplayName
    }
    return catValue
  }
  const renderCategoryDropDown = shouldRenderModificationCategory(product)
  return (
    <Grid container style={{ marginTop: '24px' }}>
      <Grid item xs={12}>
        <Typography component="span" variant="h3" data-scrub>
          {title}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography
          component="span"
          variant="body1"
          data-scrub
          className={classes.infoText}
        >
          {description}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container style={{ marginTop: '1rem' }} spacing={4}>
          {renderCategoryDropDown && (
            <Grid item xs={3}>
              <LiquidSelect
                filled
                onChange={onCategoryChange}
                value={
                  categoryValue === '' && !categoryValue
                    ? undefined
                    : categoryValue
                }
                displayEmpty
                disabled={disabled}
                renderValue={() => renderCategoryValue(categoryValue)}
              >
                {categoryList.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.categoryDisplayName}
                  </MenuItem>
                ))}
              </LiquidSelect>
            </Grid>
          )}
          <Grid item xs={4}>
            <LiquidSelect
              filled
              onChange={onModificationChange}
              value={value}
              displayEmpty
              disabled={disabled}
              renderValue={() => renderModValue(value)}
            >
              {modificationList.map((mod) => (
                <MenuItem key={mod?.value} value={mod?.value}>
                  {mod.displayName}
                </MenuItem>
              ))}
            </LiquidSelect>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default ModificationDropDownBase
